<template>
  <div>
    <!-- Invite User to Access Modal -->
    <hl-Modal
      v-if="isInviteUserToAccessModalShown"
      class="text-center"
      iconClass="budicon-user-b text-primary"
      title="Inviter ny bruker"
    >
      <ValidationObserver v-slot="{ invalid }">
        <p>
          Personen vil motta invitasjon til å registrere seg som bruker. Når registreringen er fullført vil brukeren ha
          tilgang til utleierprofilen.
        </p>

        <div class="row">
          <div class="col">
            <ValidationProvider
              name="Navn"
              :rules="validationRules.landlordProfile.inviteUserModal.fullName"
              v-slot="{ errors }"
            >
              <hl-Input type="text" label="Navn" placeholder v-model="invitedUser.name" :error="errors[0]"></hl-Input>
            </ValidationProvider>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col">
            <ValidationProvider
              name="E-post"
              :rules="validationRules.landlordProfile.inviteUserModal.email"
              v-slot="{ errors }"
            >
              <hl-Input type="email" label="E-post" v-model="invitedUser.email" :error="errors[0]"></hl-Input>
            </ValidationProvider>
          </div>

          <div class="col">
            <ValidationProvider
              name="Gjenta e-post"
              :rules="validationRules.landlordProfile.inviteUserModal.repeatEmail"
              v-slot="{ errors }"
            >
              <hl-Input
                type="email"
                label="Gjenta e-post"
                v-model="invitedUser.repeatEmail"
                :error="errors[0]"
              ></hl-Input>
            </ValidationProvider>
          </div>
        </div>

        <div class="mt-5">
          <button class="btn btn-primary mx-2" @click="inviteUser" :disabled="invalid">Send invitasjon</button>
          <button class="btn btn-outline-secondary mx-2" @click="toggleInviteUserToAccessModal">Avbryt</button>
        </div>
      </ValidationObserver>
    </hl-Modal>
    <!-- Invite User to Access Modal -->

    <!-- Remove Access to User Modal -->
    <hl-Modal
      v-if="isRemoveAccessToUserModalShown"
      class="text-center"
      iconClass="budicon-block-sign text-danger"
      title="Slett brukertilgang"
    >
      <p>{{ landlordProfileUserToBeRemoved.profile.name }} ({{ landlordProfileUserToBeRemoved.profile.email }})</p>
      <p>Klikk på knappen “slett” for å slette brukerens tilgang til utleierprofilen.</p>

      <div class="mt-5">
        <button class="btn btn-danger mx-2" @click="removeUserAccessAndClose">Slett</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleRemoveAccessToUserModal">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- Remove Access to User Modal -->

    <!-- Remove Bank Account Modal -->
    <hl-Modal
      v-if="isDeleteBankAccountModalShown"
      class="text-center"
      iconClass="budicon-withdraw-money text-danger"
      title="Slett bankkonto"
    >
      <p>Ved å slette denne bankkontoen vil den fjernes fra leieforholdene den er knyttet til.</p>

      <div class="mt-5">
        <button class="btn btn-danger mx-2" @click="deleteAccount">Slett</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleDeleteBankAccountModal">Avbryt</button>
      </div>
    </hl-Modal>
    <!-- Remove Bank Account Modal -->

    <!-- See Bank Account Details Modal -->
    <hl-Modal
      v-if="isEditBankAccountModalShown"
      class="text-center"
      iconClass="budicon-withdraw-money text-primary"
      title="Edit bank account"
    >
      <p>accounts in use...</p>

      <div class="mt-5">
        <button class="btn btn-primary mx-2">Edit</button>
        <button class="btn btn-outline-danger mx-2" @click="deleteAccount">Delete</button>
        <button class="btn btn-outline-secondary mx-2" @click="toggleEditBankAccountModal">Cancel</button>
      </div>
    </hl-Modal>
    <!-- See Bank Account Details Modal -->

    <!-- Add Bank Account Modal -->
    <hl-Modal
      v-if="isAddBankAccountModalShown"
      class="text-center"
      iconClass="budicon-withdraw-money text-primary"
      title="Legg til ny bankkonto"
    >
      <ValidationObserver v-slot="{ invalid }">
        <p>
          Bankkontoen vil benyttes for utbetaling av husleie til utleier. Det kan benyttes ulike bankkonto for ulike
          leieforhold.
        </p>

        <ValidationProvider
          name="Bankkonto"
          :rules="validationRules.landlordProfile.preferences.bankAccountNumber"
          v-slot="{ errors }"
        >
          <hl-Input
            placeholder="4444.11.55546"
            label="Bankkonto"
            v-model="newBankAccount.account"
            v-mask="'####.##.#####'"
            :error="errors[0]"
          ></hl-Input>
        </ValidationProvider>

        <hl-Input
          type="text"
          class="mt-4"
          placeholder="Husleiekonto"
          label="Alias"
          v-model="newBankAccount.alias"
        ></hl-Input>

        <div class="mt-5">
          <button class="btn btn-primary mx-2" @click="addAccount" :disabled="invalid">Legg til</button>
          <button class="btn btn-outline-secondary mx-2" @click="toggleAddBankAccountModal">Avbryt</button>
        </div>
      </ValidationObserver>
    </hl-Modal>
    <!-- Add Bank Account Modal -->

    <InfoCard>
      I Preferanser kan du se og endre hvilke bankkontoer som benyttes for utbetaling av husleien, samt definere
      husordensregler og bestemmelser som skal brukes i dine husleiekontrakter som opprettes fremover. Du kan også
      definere en utleierprofil som standard, dvs at det er denne du ser først når du logger inn på Husleie.no.
    </InfoCard>

    <div class="row mt-4">
      <div class="col">
        <BlockContainer class="p-5">
          <div class="row">
            <div class="col">
              <h5>Bankkonto</h5>
            </div>

            <div v-if="landlordProfileBankAccounts && landlordProfileBankAccounts.length" class="col text-right">
              <button class="btn btn-sm btn-outline-secondary" @click="toggleAddBankAccountModal">Ny bankkonto</button>
            </div>
          </div>

          <div v-if="loading.accounts" class="text-center my-5">
            <hl-Loader></hl-Loader>
          </div>

          <List v-else-if="landlordProfileBankAccounts && landlordProfileBankAccounts.length" class="mt-5">
            <p class="text-secondary">Bankkonto</p>
            <BankAccountCard
              v-for="(account, i) in landlordProfileBankAccounts"
              :key="i"
              :account="account"
              @click:delete="showDeleteBankAccountModal(account)"
            ></BankAccountCard>
          </List>

          <div v-if="landlordProfileBankAccounts && landlordProfileBankAccounts.length === 0" class="mt-5 text-center">
            <img src="@/assets/empty-bank-account.svg" width="210" />

            <h6 class="mt-5">Vennligst legg til en bankkonto for utbetaling av husleie</h6>

            <hl-Button class="mt-3" variant="secondary" size="sm" @click="toggleAddBankAccountModal"
              >Legg til en bankkonto</hl-Button
            >
          </div>
        </BlockContainer>
      </div>

      <div class="col">
        <BlockContainer class="p-5">
          <div class="row">
            <div class="col-8">
              <h5>
                Husordensregler
                <hl-HelpTooltip
                  >Standard husordensregler. Disse blir automatisk importert når du lager en ny
                  leiekontrakt.</hl-HelpTooltip
                >
              </h5>
            </div>

            <div class="col text-right">
              <SaveButton
                :disabled="profileDefaultsLoading"
                :loading="profileDefaultsLoading"
                @click.native="saveLandlordProfileDefaults"
              ></SaveButton>
            </div>
          </div>

          <div class="row mt-4">
            <div class="col">
              <h6>Dyrehold</h6>
              <hl-Switch
                class="mt-4"
                v-model="landlordProfileDefaults.animalsAllowed"
                showState
                onState="Tillatt"
                offState="Ikke tillatt"
              ></hl-Switch>
            </div>

            <div class="col">
              <h6>Røyking</h6>
              <hl-Switch
                class="mt-4"
                v-model="landlordProfileDefaults.smokingAllowed"
                showState
                onState="Tillatt"
                offState="Ikke tillatt"
              ></hl-Switch>
            </div>
          </div>

          <div class="mt-5">
            <h6>Husordensregler</h6>
            <hl-Textarea v-model="landlordProfileDefaults.houseRules" :rows="6"></hl-Textarea>
          </div>

          <div class="mt-4">
            <h6>Særlige bestemmelser</h6>
            <hl-Textarea v-model="landlordProfileDefaults.specialRemarks" :rows="6"></hl-Textarea>
          </div>
        </BlockContainer>
      </div>
    </div>

    <div class="pt-4 mt-2">
      <BlockContainer class="p-5">
        <div class="row align-items-center">
          <div class="col">
            <h5>Brukere med tilgang til utleierprofil</h5>
          </div>

          <div class="col text-right">
            <button class="btn btn-sm btn-outline-secondary" @click="toggleInviteUserToAccessModal">
              Gi andre tilgang
            </button>
          </div>
        </div>

        <List class="mt-5">
          <ListHeader>
            <div class="col-4">Navn</div>

            <div class="col-4">E-post</div>

            <div class="col">Tilgang gitt</div>

            <div class="col text-center"></div>
          </ListHeader>

          <ListItem v-for="(user, i) in getLandlordProfileUsers" :key="i">
            <div class="col-4">
              <NameInitials class="mr-2" :name="user.profile.name"></NameInitials>
              {{ user.profile.name }}
            </div>

            <div class="col-4 text-secondary">{{ user.profile.email }}</div>

            <div class="col text-secondary">
              <hl-Date :date="user.accessGranted"></hl-Date>
            </div>

            <div class="col text-center">
              <hl-Button
                v-if="user.role !== 'owner'"
                variant="white"
                size="sm"
                @click="showRemoveAccessToUserModal(user)"
              >
                <i class="budicon-trash"></i>
              </hl-Button>
            </div>
          </ListItem>
        </List>
      </BlockContainer>
    </div>

    <BlockContainer class="p-5 mt-5">
      <h5 class="mb-4">Standard utleierprofil</h5>
      <p>
        Ved å velge en utleierprofil som "Standard utleierprofil" vil det være leieforholdene som tilhører denne
        utleierprofilen du alltid vil se først når du går til siden "Leieforhold". For å sette en annen utleierprofil
        som "Standard utleierprofil" så må du navigere tilbake til oversiktssiden for utleierprofiler og deretter besøke
        "preferanser" for den valgte utleierprofilen.
      </p>
      <hl-Button class="mt-4" :disabled="defaultLandlordId === getLandlordId" @click="setAsDefaultLandlord">
        <span v-if="defaultLandlordId === getLandlordId">Allerde valgt som standardprofil</span>
        <span v-else>Velg som standardprofil</span>
      </hl-Button>
    </BlockContainer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { mapFields } from 'vuex-map-fields';
import BlockContainer from '@/components/BlockContainer.vue';
import BankAccountCard from '@/components/molecules/settings/BankAccountCard';
import SaveButton from '@/components/UI/buttons/SaveButton';
import addValidationRules from '@/mixins/addValidationRules.js';
import NameInitials from '@/components/UI/NameInitials.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Switch from '@modules/husleie-framework/src/components/forms/inputs/switch/Switch.vue';
import Textarea from '@modules/husleie-framework/src/components/forms/Textarea.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal.vue';
import HelpTooltip from '@modules/husleie-framework/src/components/tooltips/HelpTooltip.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import List from '@/components/UI/list/List.vue';
import ListHeader from '@/components/UI/list/ListHeader.vue';
import ListItem from '@/components/UI/list/ListItem.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import { ProfilesUserApi } from '@modules/husleieapi2';
import InfoCard from '@/components/InfoCard.vue';

export default {
  components: {
    BankAccountCard,
    BlockContainer,
    List,
    ListHeader,
    ListItem,
    NameInitials,
    SaveButton,
    InfoCard,
    'hl-Modal': Modal,
    'hl-Button': Button,
    'hl-Input': Input,
    'hl-Loader': Loader,
    'hl-HelpTooltip': HelpTooltip,
    'hl-Switch': Switch,
    'hl-Textarea': Textarea,
    'hl-Date': Date,
  },
  props: {
    landlordId: [String, Number],
    scrollTo: String,
  },
  mixins: [addValidationRules, i18nCommon],
  data() {
    return {
      landlordProfileDefaults: [],
      landlordProfileBankAccounts: null,
      loading: {
        accounts: null,
        debtCollection: null,
      },
      currentBankAccount: null,
      animalsAllowed: false,
      smokingAllowed: false,
      houseRules: '',
      specialRemarks: '',
      newBankAccount: {
        account: '',
        alias: '',
      },
      profileDefaultsLoading: false,
      landlordProfileUserToBeRemoved: null,
      invitedUser: {
        name: '',
        email: '',
        repeatEmail: '',
      },
    };
  },
  computed: {
    ...mapGetters([
      'isInviteUserToAccessModalShown',
      'isRemoveAccessToUserModalShown',
      'isDeleteBankAccountModalShown',
      'getLandlordBankAccountList',
      'isAddBankAccountModalShown',
      'isEditBankAccountModalShown',
      'getWhoAmI',
      'getLandlordId',
      'getLandlordProfileUsers',
    ]),

    ...mapFields([]),

    defaultLandlordId() {
      return this.getWhoAmI.preferences.defaultLandlordId;
    },
    translationErrorCouldNotRetrieveBankAccounts() {
      return this.translate('error.could not retrieve bank account');
    },
  },
  methods: {
    ...mapActions([
      'toggleDeleteBankAccountModal',
      'deleteBankAccount',
      'toggleRemoveAccessToUserModal',
      'toggleAddBankAccountModal',
      'addLandlordProfileBankAccount',
      'deleteLandlordProfileBankAccount',
      'toggleEditBankAccountModal',
      'toggleInkassoTermsModal',
      'loadLandlordProfileDefaults',
      'patchLandlordProfileDefaults',
      'loadLandlordProfileBankAccounts',
      'whoAmI',
      'toggleInviteUserToAccessModal',
      'loadLandlordProfileUsers',
      'addLandlordProfileUser',
      'deleteLandlordProfileUser',
    ]),

    showDeleteBankAccountModal(account) {
      this.currentBankAccount = account;
      this.toggleDeleteBankAccountModal();
    },
    async loadAccounts() {
      this.loading.accounts = true;
      try {
        const res = await this.loadLandlordProfileBankAccounts();
        this.landlordProfileBankAccounts = res;
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotRetrieveBankAccounts, 'error');
      } finally {
        this.loading.accounts = false;
      }
    },
    async deleteAccount() {
      try {
        await this.deleteLandlordProfileBankAccount(this.currentBankAccount.id);
        this.loadAccounts();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke slette konto.', 'error');
      }

      this.toggleDeleteBankAccountModal();
    },
    async addAccount() {
      try {
        await this.addLandlordProfileBankAccount({
          alias: this.newBankAccount.alias,
          bankAccount: this.newBankAccount.account.replace(/\D/g, ''),
        });
        this.newBankAccount = { account: '', alias: '' };
        this.loadAccounts();
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke opprette konto.', 'error');
      }

      this.toggleAddBankAccountModal();
    },
    async inviteUser() {
      if (this.invitedUser.email === this.invitedUser.repeatEmail) {
        try {
          await this.addLandlordProfileUser({
            name: this.invitedUser.name,
            email: this.invitedUser.email,
            role: 'user',
          });
          this.invitedUser = { name: '', email: '', repeatEmail: '' };
          this.toggleInviteUserToAccessModal();
        } catch (error) {
          console.error(error);
          this.$toast('Kunne ikke invitere bruker.', 'error');
        }
      }
    },
    async removeUserAccessAndClose() {
      if (this.landlordProfileUserToBeRemoved) {
        try {
          await this.deleteLandlordProfileUser(this.landlordProfileUserToBeRemoved.id);
        } catch (error) {
          console.error(error);
          this.$toast('Kunne ikke slette bruker.', 'error');
        }
        this.landlordProfileUserToBeRemoved = null;
        this.toggleRemoveAccessToUserModal();
      }
    },
    updatingLandlordProfileDefaults(vals) {
      for (const v of vals) {
        if (v.key === 'animalsAllowed' || v.key === 'smokingAllowed') {
          this.$set(this.landlordProfileDefaults, v.key, v.value === '1');
        } else {
          this.$set(this.landlordProfileDefaults, v.key, v.value);
        }
      }
    },
    async saveLandlordProfileDefaults() {
      this.profileDefaultsLoading = true;
      const vals = [];

      vals.push({
        key: 'animalsAllowed',
        value: this.landlordProfileDefaults.animalsAllowed ? 1 : 0,
      });

      vals.push({
        key: 'smokingAllowed',
        value: this.landlordProfileDefaults.smokingAllowed ? 1 : 0,
      });

      vals.push({
        key: 'houseRules',
        value: this.landlordProfileDefaults.houseRules,
      });

      vals.push({
        key: 'specialRemarks',
        value: this.landlordProfileDefaults.specialRemarks,
      });

      try {
        const res = await this.patchLandlordProfileDefaults(vals);
        this.updatingLandlordProfileDefaults(res);
        this.$toast('Lagret.', 'success');
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre standardinnstillinger.', 'error');
      }
      this.profileDefaultsLoading = false;
    },
    showRemoveAccessToUserModal(user) {
      this.landlordProfileUserToBeRemoved = user;
      this.toggleRemoveAccessToUserModal();
    },
    async setAsDefaultLandlord() {
      const api = new ProfilesUserApi();
      await api.setProfileUserPreference({
        body: {
          key: 'default_landlord_id',
          value: this.getLandlordId,
        },
      });
      this.$toast('Utleierprofil valgt som standardprofil.', 'success');
      await this.whoAmI();
    },
  },
  watch: {
    loading: {
      async handler() {
        if (this.loading.accounts === false && this.loading.debtCollection === false) {
          if (this.scrollTo) {
            this.$nextTick(() => {
              if (this.scrollTo === 'debtCollection') {
                this.$refs.debt_collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            });
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  async created() {
    try {
      await this.loadLandlordProfileUsers();
    } catch (error) {
      console.error(error);
      this.$toast('Kunne ikke laste brukere.', 'error');
    }
    try {
      const res = await this.loadLandlordProfileDefaults();
      this.updatingLandlordProfileDefaults(res);
    } catch (error) {
      console.error(error);
      this.$toast('Kunne ikke hente standardinnstillinger.', 'error');
    }
    this.loadAccounts();
  },
};
</script>
